import copy from "copy-to-clipboard";
import moment from "moment";
import {
  Component
} from "react";
import AccountDetails from "../../components/Accounts/Card/AccountDetails";
import Button from '../../components/Layout/Wrappers/Button';
import Checkbox from '../../components/Layout/Wrappers/Checkbox';
import IconButton from '../../components/Layout/Wrappers/IconButton';
import Menu from '../../components/Layout/Wrappers/Menu';
import {
  MODEL__NAME__ACCOUNT
} from '../../dictionaries/Models.dic';
import {
  RELATION__ACCOUNTS_STARRED
} from '../../dictionaries/Relations.dic';
import Account from "../Account";
import {
  Arr
} from '../Array.lib';
import Core from "../Core";
import {
  DATE_FORMAT__DATE__US_LONG
} from '../Date.lib';
import Definition, {
  ACCOUNT_ACTION__EDIT_ACCOUNT,
  DEFINITION_CATEGORY__ACCOUNT_ANNOUNCEMENTS,
  DEFINITION_CATEGORY__ACCOUNT_EXPERTISE,
  DEFINITION_CATEGORY__ACCOUNT_INTERACTIONS
} from "../Definition";
import mapStarred from "../mappers/EntityStarred.mapper";
import {
  mapCandidates
} from "./candidate";
import {
  mapEmployers
} from './employer';

const mdash = "—";

export function getBookmarkModel() {
  return (
    {
      createdAt: new Date().toISOString(),
      label: '',
      pathname: '', // from react-router-dom.location
      search: '', // from react-router-dom.location
    }
  );
};

export function mapBookmark(item = {}) {
  const bookmarkModel = getBookmarkModel();
  Object.keys(bookmarkModel).forEach(key => (item[key] = (item[key] || bookmarkModel[key])));
  return item;
}

export function mapBookmarks(items = []) {
  return items.map(mapBookmark);
}

function getAccountModel({ extended: requiredExtended } = {}) {

  const model = {
    firstName: "",
    lastName: "",
    role: "Admin",

    // {string}:AccountSubrole._Id
    subroleId: '',

    username: "",
    email: "",

    emailsList: [],
    excludeAutomatedEmails: 0,
    excludeAutomatedEmailsUntil: '',

    phone: "",
    preferredName: "",
    title: "",
    talentAdvisor: "",
    linkedInUrl: "",
    calendarUrl: "",
    publicNotes: "",
    privateNotes: "",

    companyName: "",
    /** @prop {boolean} leftCompanyFlag  If checked it should show an input for __newCompanyName__ on Candidate Edit */
    leftCompanyFlag: false,

    /** 
     * @prop {string} newCompanyName  If __leftCompanyFlag__ is checked, then it should show an input for this on
     * Candidate Edit
     */
    newCompanyName: '',

    additionalPhone: "",
    additionalEmail: "",
    testBucket: null,
    extensionId: "",
    closeDate: null, // "2017-12-18T20:51:55.157Z",
    expirationDate: null, // "2018-01-19T15:43:52.163Z",

    // special
    password: "",

    // useless ?
    address: "",
    employerName: "",
    employerInfo: "",
    userSettings: "",
    emailVerified: false,

    // mixin timestamps
    createdAt: null, // new Date().toISOString(), // "2017-12-18T20:51:55.157Z",
    updatedAt: null, // new Date().toISOString(), // "2017-12-18T20:51:55.157Z",

    state: 2,

    // used for layout v3
    bookmarks: [], // <object[]>

    calendarBookingLink: '', // {string}

    // {number[]}:{tagId[]}
    interactions: [],

    // {number[]}:{tagId[]}
    expertise: [],

    // {number[]}:{tagId[]}
    announcements: [],

    /** @prop {boolean} twoFactorEnabled */
    twoFactorEnabled: false,

  };

  const extended = {
    ___model___: MODEL__NAME__ACCOUNT,
    id: null,
    ...model,
    /** includes */
    candidates: [],
    employers: [],
    //accountStateTag: {},
    /** for local use */
    retype: "",
    realRoleId: "",
    roleMapId: "",
    roles: [],
    realRole: {},
    roleMap: {},
    accountRecruiterWhiteList: [],
    accountEmployerBlackList: [],
    accountJobBlackList: [],
    accountEmployerSourceList: [],
    accountJobSourceList: [],
    subrole: {},
    ___keys___: []
  };

  return requiredExtended ? extended : model;

}

const mapAccount = item => {

  const account = getAccountModel({
    extended: true
  });

  if (item) {
    Object.keys(account).forEach(
      key => !!item[key] && (account[key] = item[key])
    );
    account.id = item.id || item._id;
    account._createdAt = account.createdAt
      ? moment(account.createdAt).format(DATE_FORMAT__DATE__US_LONG)
      : mdash;
    account._updatedAt = account.updatedAt
      ? moment(account.updatedAt).format(DATE_FORMAT__DATE__US_LONG)
      : mdash;
    account.candidates = mapCandidates(account.candidates);

    account.employers = mapEmployers(account.employers);

    account.bookmarks = mapBookmarks(account.bookmarks);

    account._candidatesLength = Number(account.candidates.length);

    mapStarred({ model: account, starredList: Arr(item[RELATION__ACCOUNTS_STARRED]) });

    account._starred = account.starred ? "Starred: True" : "Starred: False";
    account._name = `${account.firstName || ""} ${account.lastName ||
      ""}`.trim();

    /* set definition labels */
    Definition.set(account, "state");

    account._infoCmp = <AccountInfo account={account} />;
    account._emailCmp = <EmailInfo account={account} />;
    account._engagementsCmp = <AccountDetails account={account} />;
    account._rowOptionsCmp = <RowOptions account={account} />;

    account.checkStar = (checked, onSuccess) => {
      Account.updateStarred(
        account.id,
        account.starredId,
        checked,
        response => {
          account.starredId = response.id;
          account.starred = response.starred;
          account._starred = response.starred ? "Starred: True" : "Starred: False";
          account.filters = {
            ...account.filters,
            Starred: ["Non Starred", "Starred"][~~Boolean(response.starred)]
          };
          onSuccess && onSuccess(response);
        }
      );
    };
    account.copyEmail = em => {
      if (
        copy(account.email, {
          debug: true,
          message: "Press #{key} to copy"
        })
      ) {
        Core.showSuccess(`${account.email} copied!`);
      } else {
        Core.showError("Fail copy!");
      }
    };
    account.delete = onSuccess => {
      const candidates = account.candidates || [];
      Core.dialog.open({
        title: (
          <>{`Delete "${account._name}"${!!candidates.length
            ? ` and ${candidates.length} candidate${candidates.length === 1 ? "" : "s"
            }?`
            : ""
            }`}</>
        ),
        message: "This action can't be undone.",
        style: { width: "320px" },
        actions: [
          <Button flat
            label="Cancel"
            className="button-white-cyan"
            onClick={ev => {
              Core.dialog.close();
            }}
          />,
          <Button flat
            label="Delete"
            className="button-flat-cyan"
            onClick={ev => {
              Core.dialog.close();
              Account.delete(account.id, onSuccess ? onSuccess : function () { });
            }}
          />
        ]
      });
    };

    Definition.map(account, DEFINITION_CATEGORY__ACCOUNT_INTERACTIONS, 'interactions');
    Definition.map(account, DEFINITION_CATEGORY__ACCOUNT_EXPERTISE, 'expertise');
    Definition.map(account, DEFINITION_CATEGORY__ACCOUNT_ANNOUNCEMENTS, 'announcements');

    /* for autocomplete */
    account.___keys___ = [
      account._state,
      account._name,
      account.companyName,
      account._starred,
      account._candidatesLength,
      account._interactions,
      account._expertise,
      account._announcements,
    ]
      /* combine and split values */
      .join("; ")
      .split("; ")
      /* remove empty values */
      .filter(e => !!e && String(e).trim());

    // console.debug('map', account);

  }


  return account;
};

const mapAccounts = data => {
  return (data || []).map(item => {
    const account = mapAccount(item);
    return {
      ...account,
      filters: {
        Name: String(String(account.lastName)[0]).toUpperCase(),
        Role: account.role,
        Recent: moment(account.createdAt),
        Starred: ["Non Starred", "Starred"][~~Boolean(account.starred)]
      }
    };
  });
};

class AccountInfo extends Component {
  render() {
    const { account } = this.props;
    return Core.isAdmin({ action: ACCOUNT_ACTION__EDIT_ACCOUNT }) ? (
      <span
        className="anchor"
        onClick={ev => Core.go({
          ...this.props,
          to: `/account/edit/${account.id}`
        })}
      >
        <b>
          {account.firstName || <i>&mdash;</i>}
          &nbsp;
          {account.lastName || <i>&mdash;</i>}
        </b>
      </span>
    ) : (
      <b>
        {account.firstName || <i>&mdash;</i>}
        &nbsp;
        {account.lastName || <i>&mdash;</i>}
      </b>
    );
  }
}
class EmailInfo extends Component {
  render() {
    const { account } = this.props;
    return (
      <>
        <IconButton
          iconStyle={account.openedColor}
          className="icon16"
          onClick={account.copyEmail}
        >
          <i className="material-icons">mail</i>
        </IconButton>
        &nbsp;
        <span className="truncate" style={{ width: "calc(100% - 48px)" }}>
          {account.email || <i>&mdash;</i>}
        </span>
      </>
    );
  }
}
class RowOptions extends Component {
  render() {
    // Core.log("RowOptions", "render");
    const { account } = this.props;
    return (
      <div className="row-options inline-blocks">
        <Checkbox
          className="starred"
          checked={account.starred}
          onCheck={(ev, checked) => {
            account.checkStar(checked, res => this.setState({ updated: true }));
          }}
          checkedIcon={<i className="material-icons">star</i>}
          uncheckedIcon={<i className="material-icons">star_border</i>}
        />
        <Menu icon
          name={`account_model__row_options__menu`}
          options={[
            {
              acl: Core.isAdminOnLocal(),
              label: 'Delete',
              onClick: (event) => account.delete(res => Core.Main && Core.Main.fetchData())
            }
          ]}
        />
      </div>
    );
  }
}

export {
  getAccountModel,
  mapAccount,
  mapAccounts
};

